/**
 * Block Kit interactive elements (core `renderKit` element nodes), shared by message blocks, app modals and App Home
 * tabs: text inputs, selects (a plain static select: `Select`; everything else — multi selects, option groups,
 * external selects and the server-searched people / conversation / channel pickers: `KitPicker`), date (Popover + `DatePicker`) and time (15-minute `Select`, like Slack's) pickers, checkboxes,
 * radio buttons, the overflow "⋯" menu, and the confirm dialog buttons and choices can ask for first.
 *
 * Values are core `KitValues` strings (option values, ids, the text, "YYYY-MM-DD", "HH:mm"); `onChange` gets the new
 * list. A choice with a `confirm` asks first and is dropped on "deny".
 */
import { BlockKit as BK, type KitContainer, type RichTextContext } from "@bundle/core";
import { useState, type ReactNode } from "react";
import { useLocale, useT } from "../i18n/I18nProvider.tsx";
import { Button, Checkbox, DatePicker, IconButton, Menu, Popover, RadioGroup, Select, Sheet, Stack, Text, TextField } from "../primitives/index.ts";
import { KitPicker } from "./BlockKitPicker.tsx";

export { selectOptions } from "./BlockKitPicker.tsx";

type KitElementNode = BK.KitElementNode;
type KitValueNode = BK.KitValueNode;
type KitConfirmNode = BK.KitConfirmNode;

/** "Are you sure?" before a choice goes to the app (Slack's confirm object). */
export function KitConfirmDialog({ confirm, onConfirm, onCancel, testID }: { confirm: KitConfirmNode | undefined; onConfirm: () => void; onCancel: () => void; testID: string }) {
  return (
    <Sheet open={confirm !== undefined} onClose={onCancel} title={confirm?.title ?? ""} detents={["medium"]} testID={testID}>
      {confirm === undefined ? null : (
        <Stack gap="4">
          <Text color="text.secondary">{confirm.text}</Text>
          <Stack direction="horizontal" gap="2" justify="end">
            <Button label={confirm.deny} variant="secondary" onPress={onCancel} testID={`${testID}-deny`} />
            <Button label={confirm.confirm} variant={confirm.style === "danger" ? "destructive" : "primary"} onPress={onConfirm} testID={`${testID}-confirm`} />
          </Stack>
        </Stack>
      )}
    </Sheet>
  );
}

/** Runs `act` at once, or after the confirm dialog when there is one. → the dialog to render and the guarded runner. */
export function useKitConfirm(testID: string): { guard: (confirm: KitConfirmNode | undefined, act: () => void) => void; dialog: ReactNode } {
  const [pending, setPending] = useState<{ confirm: KitConfirmNode; act: () => void } | undefined>(undefined);
  const guard = (confirm: KitConfirmNode | undefined, act: () => void) => (confirm === undefined ? act() : setPending({ confirm, act }));
  const dialog = (
    <KitConfirmDialog
      confirm={pending?.confirm}
      onCancel={() => setPending(undefined)}
      onConfirm={() => {
        const act = pending?.act;
        setPending(undefined);
        act?.();
      }}
      testID={`${testID}-confirm`}
    />
  );
  return { guard, dialog };
}

const TIMES: string[] = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);

function DateField(p: { value: string | undefined; onChange: (v: string) => void; placeholder: string; testID: string }) {
  const [open, setOpen] = useState(false);
  const locale = useLocale();
  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      side="bottom"
      align="start"
      anchor={<Button label={p.value ?? p.placeholder} icon="calendar" size="sm" variant="secondary" onPress={() => setOpen(!open)} testID={`${p.testID}-open`} />}
    >
      <DatePicker
        value={p.value}
        locale={locale === "pt-BR" ? "pt-BR" : "en"}
        onChange={(day) => {
          setOpen(false);
          p.onChange(day);
        }}
        testID={p.testID}
      />
    </Popover>
  );
}

export type KitElementViewProps = {
  element: KitElementNode;
  /** The element's current values (value elements). */
  value: string[];
  ctx: RichTextContext;
  /** A value element changed (after its confirm dialog, if any). */
  onChange: (value: string[]) => void;
  /** A button was pressed (after its confirm dialog). */
  onPress?: (button: BK.KitButtonNode) => void;
  /** An overflow choice was picked (after its confirm dialog). */
  onPick?: (value: string) => void;
  /** Enter in a one-line text input. */
  onEnter?: () => void;
  /** The input block's label (names the control for assistive tech). */
  label?: string;
  /** The element's block (external selects: the server finds the element by it). */
  blockId?: string;
  /** Where the blocks are (a message, a modal view, an App Home tab): external selects need it. */
  container?: KitContainer;
  error?: string;
  disabled?: boolean;
  testID: string;
};

/** One interactive element. */
export function KitElementView(p: KitElementViewProps) {
  const t = useT();
  const { guard, dialog } = useKitConfirm(p.testID);
  const e = p.element;
  const name = p.label ?? ("placeholder" in e && e.placeholder !== undefined ? e.placeholder : e.actionId);
  const change = (v: string[]) => guard("confirm" in e ? e.confirm : undefined, () => p.onChange(v));
  let control: ReactNode;
  switch (e.kind) {
    case "button":
      control = (
        <Button
          label={e.label}
          size="sm"
          variant={e.style === "primary" ? "primary" : e.style === "danger" ? "destructive" : "secondary"}
          icon={e.url !== undefined ? "externalLink" : undefined}
          disabled={p.onPress === undefined || p.disabled === true}
          onPress={() => guard(e.confirm, () => p.onPress?.(e))}
          aria-label={e.accessibilityLabel}
          testID={p.testID}
        />
      );
      break;
    case "overflow":
      control = (
        <Menu items={e.options.map((o) => ({ id: o.value, label: o.label, onSelect: () => guard(e.confirm, () => p.onPick?.(o.value)) }))} testID={p.testID}>
          <IconButton icon="more" size="sm" accessibilityLabel={t("blockKit.overflow.label")} disabled={p.onPick === undefined || p.disabled === true} onPress={() => undefined} testID={`${p.testID}-open`} />
        </Menu>
      );
      break;
    case "text_input":
      control = (
        <TextField
          value={p.value[0] ?? ""}
          onChange={(v) => p.onChange(v === "" ? [] : [v])}
          multiline={e.multiline}
          placeholder={e.placeholder}
          accessibilityLabel={name}
          autoFocus={e.focus}
          error={p.error}
          disabled={p.disabled}
          onSubmit={e.multiline ? undefined : p.onEnter}
          testID={p.testID}
        />
      );
      break;
    case "select": {
      const placeholder = e.placeholder ?? t(
        e.source === "users" ? (e.multi ? "blockKit.multiUsers.placeholder" : "blockKit.users.placeholder")
          : e.source === "channels" ? "blockKit.channels.placeholder"
            : e.source === "conversations" ? "blockKit.conversations.placeholder"
              : e.multi ? "blockKit.multiSelect.placeholder" : "blockKit.select.placeholder",
      );
      control = e.multi || e.source !== "static" || e.groups !== undefined ? (
        <KitPicker node={e} value={p.value} onChange={change} placeholder={placeholder} label={name} ctx={p.ctx} blockId={p.blockId} container={p.container} disabled={p.disabled} error={p.error} testID={p.testID} />
      ) : (
        <Select
          value={p.value[0]}
          onChange={(v) => change([v])}
          options={e.options.map((o) => ({ value: o.value, label: o.label }))}
          placeholder={placeholder}
          accessibilityLabel={name}
          error={p.error}
          disabled={p.disabled}
          testID={p.testID}
        />
      );
      break;
    }
    case "datepicker":
      control = <DateField value={p.value[0]} onChange={(v) => change([v])} placeholder={e.placeholder ?? t("blockKit.date.placeholder")} testID={p.testID} />;
      break;
    case "timepicker": {
      const current = p.value[0];
      const times = current !== undefined && !TIMES.includes(current) ? [...TIMES, current].sort() : TIMES;
      control = (
        <Select value={current} onChange={(v) => change([v])} options={times.map((x) => ({ value: x, label: x }))} placeholder={e.placeholder ?? t("blockKit.time.placeholder")} accessibilityLabel={name} error={p.error} disabled={p.disabled} testID={p.testID} />
      );
      break;
    }
    case "checkboxes":
      control = (
        <Stack gap="1" testID={p.testID}>
          {e.options.map((o) => (
            <Stack key={o.value} gap="0">
              <Checkbox
                label={o.label}
                value={p.value.includes(o.value)}
                disabled={p.disabled}
                onChange={(on) => change(on ? [...p.value, o.value] : p.value.filter((x) => x !== o.value))}
                testID={`${p.testID}-${o.value}`}
              />
              {o.description === undefined ? null : (
                <Text variant="caption" color="text.secondary">
                  {o.description}
                </Text>
              )}
            </Stack>
          ))}
        </Stack>
      );
      break;
    case "radio_buttons":
      control = (
        <RadioGroup
          value={p.value[0]}
          onChange={(v) => change([v])}
          options={e.options.map((o) => (o.description === undefined ? { value: o.value, label: o.label } : { value: o.value, label: o.label, description: o.description }))}
          accessibilityLabel={name}
          disabled={p.disabled}
          testID={p.testID}
        />
      );
      break;
  }
  return (
    <>
      {control}
      {dialog}
    </>
  );
}

/** True for elements that hold a value (everything but buttons and overflow menus). */
export const isValueElement = (e: KitElementNode): e is KitValueNode => e.kind !== "button" && e.kind !== "overflow";
